import {
  prepareEvent,
  prepareContractCall,
  readContract,
  type BaseTransactionOptions,
  type AbiParameterToPrimitiveType,
} from 'thirdweb'

/**
 * Contract events
 */

/**
 * Represents the filters for the "Approval" event.
 */
export type ApprovalEventFilters = Partial<{
  owner: AbiParameterToPrimitiveType<{
    indexed: true
    internalType: 'address'
    name: 'owner'
    type: 'address'
  }>
  approved: AbiParameterToPrimitiveType<{
    indexed: true
    internalType: 'address'
    name: 'approved'
    type: 'address'
  }>
  tokenId: AbiParameterToPrimitiveType<{
    indexed: true
    internalType: 'uint256'
    name: 'tokenId'
    type: 'uint256'
  }>
}>

/**
 * Creates an event object for the Approval event.
 * @param filters - Optional filters to apply to the event.
 * @returns The prepared event object.
 * @example
 * ```
 * import { getContractEvents } from "thirdweb";
 * import { approvalEvent } from "TODO";
 *
 * const events = await getContractEvents({
 * contract,
 * events: [
 *  approvalEvent({
 *  owner: ...,
 *  approved: ...,
 *  tokenId: ...,
 * })
 * ],
 * });
 * ```
 */
export function approvalEvent(filters: ApprovalEventFilters = {}) {
  return prepareEvent({
    signature:
      'event Approval(address indexed owner, address indexed approved, uint256 indexed tokenId)',
    filters,
  })
}

/**
 * Represents the filters for the "ApprovalForAll" event.
 */
export type ApprovalForAllEventFilters = Partial<{
  owner: AbiParameterToPrimitiveType<{
    indexed: true
    internalType: 'address'
    name: 'owner'
    type: 'address'
  }>
  operator: AbiParameterToPrimitiveType<{
    indexed: true
    internalType: 'address'
    name: 'operator'
    type: 'address'
  }>
}>

/**
 * Creates an event object for the ApprovalForAll event.
 * @param filters - Optional filters to apply to the event.
 * @returns The prepared event object.
 * @example
 * ```
 * import { getContractEvents } from "thirdweb";
 * import { approvalForAllEvent } from "TODO";
 *
 * const events = await getContractEvents({
 * contract,
 * events: [
 *  approvalForAllEvent({
 *  owner: ...,
 *  operator: ...,
 * })
 * ],
 * });
 * ```
 */
export function approvalForAllEvent(filters: ApprovalForAllEventFilters = {}) {
  return prepareEvent({
    signature:
      'event ApprovalForAll(address indexed owner, address indexed operator, bool approved)',
    filters,
  })
}

/**
 * Represents the filters for the "LogEvent" event.
 */
export type LogEventEventFilters = Partial<{
  sender: AbiParameterToPrimitiveType<{
    indexed: true
    internalType: 'address'
    name: 'sender'
    type: 'address'
  }>
}>

/**
 * Creates an event object for the LogEvent event.
 * @param filters - Optional filters to apply to the event.
 * @returns The prepared event object.
 * @example
 * ```
 * import { getContractEvents } from "thirdweb";
 * import { logEventEvent } from "TODO";
 *
 * const events = await getContractEvents({
 * contract,
 * events: [
 *  logEventEvent({
 *  sender: ...,
 * })
 * ],
 * });
 * ```
 */
export function logEventEvent(filters: LogEventEventFilters = {}) {
  return prepareEvent({
    signature:
      'event LogEvent(address indexed sender, uint256 value, string message)',
    filters,
  })
}

/**
 * Creates an event object for the Pause event.
 * @returns The prepared event object.
 * @example
 * ```
 * import { getContractEvents } from "thirdweb";
 * import { pauseEvent } from "TODO";
 *
 * const events = await getContractEvents({
 * contract,
 * events: [
 *  pauseEvent()
 * ],
 * });
 * ```
 */
export function pauseEvent() {
  return prepareEvent({
    signature: 'event Pause()',
  })
}

/**
 * Creates an event object for the SaleComplete event.
 * @returns The prepared event object.
 * @example
 * ```
 * import { getContractEvents } from "thirdweb";
 * import { saleCompleteEvent } from "TODO";
 *
 * const events = await getContractEvents({
 * contract,
 * events: [
 *  saleCompleteEvent()
 * ],
 * });
 * ```
 */
export function saleCompleteEvent() {
  return prepareEvent({
    signature:
      'event SaleComplete(uint256 tokenId, uint256 price, uint256 fee)',
  })
}

/**
 * Represents the filters for the "Transfer" event.
 */
export type TransferEventFilters = Partial<{
  from: AbiParameterToPrimitiveType<{
    indexed: true
    internalType: 'address'
    name: 'from'
    type: 'address'
  }>
  to: AbiParameterToPrimitiveType<{
    indexed: true
    internalType: 'address'
    name: 'to'
    type: 'address'
  }>
  tokenId: AbiParameterToPrimitiveType<{
    indexed: true
    internalType: 'uint256'
    name: 'tokenId'
    type: 'uint256'
  }>
}>

/**
 * Creates an event object for the Transfer event.
 * @param filters - Optional filters to apply to the event.
 * @returns The prepared event object.
 * @example
 * ```
 * import { getContractEvents } from "thirdweb";
 * import { transferEvent } from "TODO";
 *
 * const events = await getContractEvents({
 * contract,
 * events: [
 *  transferEvent({
 *  from: ...,
 *  to: ...,
 *  tokenId: ...,
 * })
 * ],
 * });
 * ```
 */
export function transferEvent(filters: TransferEventFilters = {}) {
  return prepareEvent({
    signature:
      'event Transfer(address indexed from, address indexed to, uint256 indexed tokenId)',
    filters,
  })
}

/**
 * Creates an event object for the Unpause event.
 * @returns The prepared event object.
 * @example
 * ```
 * import { getContractEvents } from "thirdweb";
 * import { unpauseEvent } from "TODO";
 *
 * const events = await getContractEvents({
 * contract,
 * events: [
 *  unpauseEvent()
 * ],
 * });
 * ```
 */
export function unpauseEvent() {
  return prepareEvent({
    signature: 'event Unpause()',
  })
}

/**
 * Contract read functions
 */

/**
 * Represents the parameters for the "balanceOf" function.
 */
export type BalanceOfParams = {
  owner: AbiParameterToPrimitiveType<{
    internalType: 'address'
    name: 'owner'
    type: 'address'
  }>
}

/**
 * Calls the "balanceOf" function on the contract.
 * @param options - The options for the balanceOf function.
 * @returns The parsed result of the function call.
 * @example
 * ```
 * import { balanceOf } from "TODO";
 *
 * const result = await balanceOf({
 *  owner: ...,
 * });
 *
 * ```
 */
export async function balanceOf(
  options: BaseTransactionOptions<BalanceOfParams>
) {
  return readContract({
    contract: options.contract,
    method: [
      '0x70a08231',
      [
        {
          internalType: 'address',
          name: 'owner',
          type: 'address',
        },
      ],
      [
        {
          internalType: 'uint256',
          name: '',
          type: 'uint256',
        },
      ],
    ],
    params: [options.owner],
  })
}

/**
 * Calls the "baseURI" function on the contract.
 * @param options - The options for the baseURI function.
 * @returns The parsed result of the function call.
 * @example
 * ```
 * import { baseURI } from "TODO";
 *
 * const result = await baseURI();
 *
 * ```
 */
export async function baseURI(options: BaseTransactionOptions) {
  return readContract({
    contract: options.contract,
    method: [
      '0x6c0360eb',
      [],
      [
        {
          internalType: 'string',
          name: '',
          type: 'string',
        },
      ],
    ],
    params: [],
  })
}

/**
 * Represents the parameters for the "calculateTransferFee" function.
 */
export type CalculateTransferFeeParams = {
  total: AbiParameterToPrimitiveType<{
    internalType: 'uint256'
    name: 'total'
    type: 'uint256'
  }>
}

/**
 * Calls the "calculateTransferFee" function on the contract.
 * @param options - The options for the calculateTransferFee function.
 * @returns The parsed result of the function call.
 * @example
 * ```
 * import { calculateTransferFee } from "TODO";
 *
 * const result = await calculateTransferFee({
 *  total: ...,
 * });
 *
 * ```
 */
export async function calculateTransferFee(
  options: BaseTransactionOptions<CalculateTransferFeeParams>
) {
  return readContract({
    contract: options.contract,
    method: [
      '0x37800bde',
      [
        {
          internalType: 'uint256',
          name: 'total',
          type: 'uint256',
        },
      ],
      [
        {
          internalType: 'uint256',
          name: '',
          type: 'uint256',
        },
      ],
    ],
    params: [options.total],
  })
}

/**
 * Calls the "executiveOfficerAddress" function on the contract.
 * @param options - The options for the executiveOfficerAddress function.
 * @returns The parsed result of the function call.
 * @example
 * ```
 * import { executiveOfficerAddress } from "TODO";
 *
 * const result = await executiveOfficerAddress();
 *
 * ```
 */
export async function executiveOfficerAddress(options: BaseTransactionOptions) {
  return readContract({
    contract: options.contract,
    method: [
      '0x0015be71',
      [],
      [
        {
          internalType: 'address payable',
          name: '',
          type: 'address',
        },
      ],
    ],
    params: [],
  })
}

/**
 * Calls the "financialOfficerAddress" function on the contract.
 * @param options - The options for the financialOfficerAddress function.
 * @returns The parsed result of the function call.
 * @example
 * ```
 * import { financialOfficerAddress } from "TODO";
 *
 * const result = await financialOfficerAddress();
 *
 * ```
 */
export async function financialOfficerAddress(options: BaseTransactionOptions) {
  return readContract({
    contract: options.contract,
    method: [
      '0xabe088a7',
      [],
      [
        {
          internalType: 'address payable',
          name: '',
          type: 'address',
        },
      ],
    ],
    params: [],
  })
}

/**
 * Represents the parameters for the "getApproved" function.
 */
export type GetApprovedParams = {
  tokenId: AbiParameterToPrimitiveType<{
    internalType: 'uint256'
    name: 'tokenId'
    type: 'uint256'
  }>
}

/**
 * Calls the "getApproved" function on the contract.
 * @param options - The options for the getApproved function.
 * @returns The parsed result of the function call.
 * @example
 * ```
 * import { getApproved } from "TODO";
 *
 * const result = await getApproved({
 *  tokenId: ...,
 * });
 *
 * ```
 */
export async function getApproved(
  options: BaseTransactionOptions<GetApprovedParams>
) {
  return readContract({
    contract: options.contract,
    method: [
      '0x081812fc',
      [
        {
          internalType: 'uint256',
          name: 'tokenId',
          type: 'uint256',
        },
      ],
      [
        {
          internalType: 'address',
          name: '',
          type: 'address',
        },
      ],
    ],
    params: [options.tokenId],
  })
}

/**
 * Calls the "getMintPrice" function on the contract.
 * @param options - The options for the getMintPrice function.
 * @returns The parsed result of the function call.
 * @example
 * ```
 * import { getMintPrice } from "TODO";
 *
 * const result = await getMintPrice();
 *
 * ```
 */
export async function getMintPrice(options: BaseTransactionOptions) {
  return readContract({
    contract: options.contract,
    method: [
      '0xa7f93ebd',
      [],
      [
        {
          internalType: 'uint256',
          name: '',
          type: 'uint256',
        },
      ],
    ],
    params: [],
  })
}

/**
 * Calls the "getNumberTokensForSale" function on the contract.
 * @param options - The options for the getNumberTokensForSale function.
 * @returns The parsed result of the function call.
 * @example
 * ```
 * import { getNumberTokensForSale } from "TODO";
 *
 * const result = await getNumberTokensForSale();
 *
 * ```
 */
export async function getNumberTokensForSale(options: BaseTransactionOptions) {
  return readContract({
    contract: options.contract,
    method: [
      '0x305fbeb1',
      [],
      [
        {
          internalType: 'uint256',
          name: '',
          type: 'uint256',
        },
      ],
    ],
    params: [],
  })
}

/**
 * Represents the parameters for the "getSalePrice" function.
 */
export type GetSalePriceParams = {
  tokenId: AbiParameterToPrimitiveType<{
    internalType: 'uint256'
    name: 'tokenId'
    type: 'uint256'
  }>
}

/**
 * Calls the "getSalePrice" function on the contract.
 * @param options - The options for the getSalePrice function.
 * @returns The parsed result of the function call.
 * @example
 * ```
 * import { getSalePrice } from "TODO";
 *
 * const result = await getSalePrice({
 *  tokenId: ...,
 * });
 *
 * ```
 */
export async function getSalePrice(
  options: BaseTransactionOptions<GetSalePriceParams>
) {
  return readContract({
    contract: options.contract,
    method: [
      '0xf8eb5fc5',
      [
        {
          internalType: 'uint256',
          name: 'tokenId',
          type: 'uint256',
        },
      ],
      [
        {
          internalType: 'uint256',
          name: '',
          type: 'uint256',
        },
      ],
    ],
    params: [options.tokenId],
  })
}

/**
 * Represents the parameters for the "getStatus" function.
 */
export type GetStatusParams = {
  tokenId: AbiParameterToPrimitiveType<{
    internalType: 'uint256'
    name: 'tokenId'
    type: 'uint256'
  }>
}

/**
 * Calls the "getStatus" function on the contract.
 * @param options - The options for the getStatus function.
 * @returns The parsed result of the function call.
 * @example
 * ```
 * import { getStatus } from "TODO";
 *
 * const result = await getStatus({
 *  tokenId: ...,
 * });
 *
 * ```
 */
export async function getStatus(
  options: BaseTransactionOptions<GetStatusParams>
) {
  return readContract({
    contract: options.contract,
    method: [
      '0x5c622a0e',
      [
        {
          internalType: 'uint256',
          name: 'tokenId',
          type: 'uint256',
        },
      ],
      [
        {
          internalType: 'uint256',
          name: '',
          type: 'uint256',
        },
      ],
    ],
    params: [options.tokenId],
  })
}

/**
 * Represents the parameters for the "getTokenExchangeRate" function.
 */
export type GetTokenExchangeRateParams = {
  tokenId: AbiParameterToPrimitiveType<{
    internalType: 'uint256'
    name: 'tokenId'
    type: 'uint256'
  }>
}

/**
 * Calls the "getTokenExchangeRate" function on the contract.
 * @param options - The options for the getTokenExchangeRate function.
 * @returns The parsed result of the function call.
 * @example
 * ```
 * import { getTokenExchangeRate } from "TODO";
 *
 * const result = await getTokenExchangeRate({
 *  tokenId: ...,
 * });
 *
 * ```
 */
export async function getTokenExchangeRate(
  options: BaseTransactionOptions<GetTokenExchangeRateParams>
) {
  return readContract({
    contract: options.contract,
    method: [
      '0xb41f2d0c',
      [
        {
          internalType: 'uint256',
          name: 'tokenId',
          type: 'uint256',
        },
      ],
      [
        {
          internalType: 'uint256',
          name: '',
          type: 'uint256',
        },
      ],
    ],
    params: [options.tokenId],
  })
}

/**
 * Represents the parameters for the "getTokenId" function.
 */
export type GetTokenIdParams = {
  guid: AbiParameterToPrimitiveType<{
    internalType: 'bytes32'
    name: '_guid'
    type: 'bytes32'
  }>
}

/**
 * Calls the "getTokenId" function on the contract.
 * @param options - The options for the getTokenId function.
 * @returns The parsed result of the function call.
 * @example
 * ```
 * import { getTokenId } from "TODO";
 *
 * const result = await getTokenId({
 *  guid: ...,
 * });
 *
 * ```
 */
export async function getTokenId(
  options: BaseTransactionOptions<GetTokenIdParams>
) {
  return readContract({
    contract: options.contract,
    method: [
      '0xc9cb65e1',
      [
        {
          internalType: 'bytes32',
          name: '_guid',
          type: 'bytes32',
        },
      ],
      [
        {
          internalType: 'uint256',
          name: '',
          type: 'uint256',
        },
      ],
    ],
    params: [options.guid],
  })
}

/**
 * Calls the "getTokensForSale" function on the contract.
 * @param options - The options for the getTokensForSale function.
 * @returns The parsed result of the function call.
 * @example
 * ```
 * import { getTokensForSale } from "TODO";
 *
 * const result = await getTokensForSale();
 *
 * ```
 */
export async function getTokensForSale(options: BaseTransactionOptions) {
  return readContract({
    contract: options.contract,
    method: [
      '0x65e4e1c0',
      [],
      [
        {
          internalType: 'uint256[]',
          name: '',
          type: 'uint256[]',
        },
      ],
    ],
    params: [],
  })
}

/**
 * Represents the parameters for the "getWalletMintPrice" function.
 */
export type GetWalletMintPriceParams = {
  wallet: AbiParameterToPrimitiveType<{
    internalType: 'address'
    name: '_wallet'
    type: 'address'
  }>
}

/**
 * Calls the "getWalletMintPrice" function on the contract.
 * @param options - The options for the getWalletMintPrice function.
 * @returns The parsed result of the function call.
 * @example
 * ```
 * import { getWalletMintPrice } from "TODO";
 *
 * const result = await getWalletMintPrice({
 *  wallet: ...,
 * });
 *
 * ```
 */
export async function getWalletMintPrice(
  options: BaseTransactionOptions<GetWalletMintPriceParams>
) {
  return readContract({
    contract: options.contract,
    method: [
      '0xbfebbb84',
      [
        {
          internalType: 'address',
          name: '_wallet',
          type: 'address',
        },
      ],
      [
        {
          internalType: 'uint256',
          name: '',
          type: 'uint256',
        },
      ],
    ],
    params: [options.wallet],
  })
}

/**
 * Represents the parameters for the "isApprovedForAll" function.
 */
export type IsApprovedForAllParams = {
  owner: AbiParameterToPrimitiveType<{
    internalType: 'address'
    name: 'owner'
    type: 'address'
  }>
  operator: AbiParameterToPrimitiveType<{
    internalType: 'address'
    name: 'operator'
    type: 'address'
  }>
}

/**
 * Calls the "isApprovedForAll" function on the contract.
 * @param options - The options for the isApprovedForAll function.
 * @returns The parsed result of the function call.
 * @example
 * ```
 * import { isApprovedForAll } from "TODO";
 *
 * const result = await isApprovedForAll({
 *  owner: ...,
 *  operator: ...,
 * });
 *
 * ```
 */
export async function isApprovedForAll(
  options: BaseTransactionOptions<IsApprovedForAllParams>
) {
  return readContract({
    contract: options.contract,
    method: [
      '0xe985e9c5',
      [
        {
          internalType: 'address',
          name: 'owner',
          type: 'address',
        },
        {
          internalType: 'address',
          name: 'operator',
          type: 'address',
        },
      ],
      [
        {
          internalType: 'bool',
          name: '',
          type: 'bool',
        },
      ],
    ],
    params: [options.owner, options.operator],
  })
}

/**
 * Represents the parameters for the "isTokenOwner" function.
 */
export type IsTokenOwnerParams = {
  tokenId: AbiParameterToPrimitiveType<{
    internalType: 'uint256'
    name: 'tokenId'
    type: 'uint256'
  }>
  msgSender: AbiParameterToPrimitiveType<{
    internalType: 'address'
    name: 'msgSender'
    type: 'address'
  }>
}

/**
 * Calls the "isTokenOwner" function on the contract.
 * @param options - The options for the isTokenOwner function.
 * @returns The parsed result of the function call.
 * @example
 * ```
 * import { isTokenOwner } from "TODO";
 *
 * const result = await isTokenOwner({
 *  tokenId: ...,
 *  msgSender: ...,
 * });
 *
 * ```
 */
export async function isTokenOwner(
  options: BaseTransactionOptions<IsTokenOwnerParams>
) {
  return readContract({
    contract: options.contract,
    method: [
      '0xd59f2827',
      [
        {
          internalType: 'uint256',
          name: 'tokenId',
          type: 'uint256',
        },
        {
          internalType: 'address',
          name: 'msgSender',
          type: 'address',
        },
      ],
      [
        {
          internalType: 'bool',
          name: '',
          type: 'bool',
        },
      ],
    ],
    params: [options.tokenId, options.msgSender],
  })
}

/**
 * Calls the "name" function on the contract.
 * @param options - The options for the name function.
 * @returns The parsed result of the function call.
 * @example
 * ```
 * import { name } from "TODO";
 *
 * const result = await name();
 *
 * ```
 */
export async function name(options: BaseTransactionOptions) {
  return readContract({
    contract: options.contract,
    method: [
      '0x06fdde03',
      [],
      [
        {
          internalType: 'string',
          name: '',
          type: 'string',
        },
      ],
    ],
    params: [],
  })
}

/**
 * Represents the parameters for the "ownerOf" function.
 */
export type OwnerOfParams = {
  tokenId: AbiParameterToPrimitiveType<{
    internalType: 'uint256'
    name: 'tokenId'
    type: 'uint256'
  }>
}

/**
 * Calls the "ownerOf" function on the contract.
 * @param options - The options for the ownerOf function.
 * @returns The parsed result of the function call.
 * @example
 * ```
 * import { ownerOf } from "TODO";
 *
 * const result = await ownerOf({
 *  tokenId: ...,
 * });
 *
 * ```
 */
export async function ownerOf(options: BaseTransactionOptions<OwnerOfParams>) {
  return readContract({
    contract: options.contract,
    method: [
      '0x6352211e',
      [
        {
          internalType: 'uint256',
          name: 'tokenId',
          type: 'uint256',
        },
      ],
      [
        {
          internalType: 'address',
          name: '',
          type: 'address',
        },
      ],
    ],
    params: [options.tokenId],
  })
}

/**
 * Calls the "paused" function on the contract.
 * @param options - The options for the paused function.
 * @returns The parsed result of the function call.
 * @example
 * ```
 * import { paused } from "TODO";
 *
 * const result = await paused();
 *
 * ```
 */
export async function paused(options: BaseTransactionOptions) {
  return readContract({
    contract: options.contract,
    method: [
      '0x5c975abb',
      [],
      [
        {
          internalType: 'bool',
          name: '',
          type: 'bool',
        },
      ],
    ],
    params: [],
  })
}

/**
 * Represents the parameters for the "supportsInterface" function.
 */
export type SupportsInterfaceParams = {
  interfaceId: AbiParameterToPrimitiveType<{
    internalType: 'bytes4'
    name: 'interfaceId'
    type: 'bytes4'
  }>
}

/**
 * Calls the "supportsInterface" function on the contract.
 * @param options - The options for the supportsInterface function.
 * @returns The parsed result of the function call.
 * @example
 * ```
 * import { supportsInterface } from "TODO";
 *
 * const result = await supportsInterface({
 *  interfaceId: ...,
 * });
 *
 * ```
 */
export async function supportsInterface(
  options: BaseTransactionOptions<SupportsInterfaceParams>
) {
  return readContract({
    contract: options.contract,
    method: [
      '0x01ffc9a7',
      [
        {
          internalType: 'bytes4',
          name: 'interfaceId',
          type: 'bytes4',
        },
      ],
      [
        {
          internalType: 'bool',
          name: '',
          type: 'bool',
        },
      ],
    ],
    params: [options.interfaceId],
  })
}

/**
 * Calls the "symbol" function on the contract.
 * @param options - The options for the symbol function.
 * @returns The parsed result of the function call.
 * @example
 * ```
 * import { symbol } from "TODO";
 *
 * const result = await symbol();
 *
 * ```
 */
export async function symbol(options: BaseTransactionOptions) {
  return readContract({
    contract: options.contract,
    method: [
      '0x95d89b41',
      [],
      [
        {
          internalType: 'string',
          name: '',
          type: 'string',
        },
      ],
    ],
    params: [],
  })
}

/**
 * Represents the parameters for the "tokenByIndex" function.
 */
export type TokenByIndexParams = {
  index: AbiParameterToPrimitiveType<{
    internalType: 'uint256'
    name: 'index'
    type: 'uint256'
  }>
}

/**
 * Calls the "tokenByIndex" function on the contract.
 * @param options - The options for the tokenByIndex function.
 * @returns The parsed result of the function call.
 * @example
 * ```
 * import { tokenByIndex } from "TODO";
 *
 * const result = await tokenByIndex({
 *  index: ...,
 * });
 *
 * ```
 */
export async function tokenByIndex(
  options: BaseTransactionOptions<TokenByIndexParams>
) {
  return readContract({
    contract: options.contract,
    method: [
      '0x4f6ccce7',
      [
        {
          internalType: 'uint256',
          name: 'index',
          type: 'uint256',
        },
      ],
      [
        {
          internalType: 'uint256',
          name: '',
          type: 'uint256',
        },
      ],
    ],
    params: [options.index],
  })
}

/**
 * Represents the parameters for the "tokenOfOwnerByIndex" function.
 */
export type TokenOfOwnerByIndexParams = {
  owner: AbiParameterToPrimitiveType<{
    internalType: 'address'
    name: 'owner'
    type: 'address'
  }>
  index: AbiParameterToPrimitiveType<{
    internalType: 'uint256'
    name: 'index'
    type: 'uint256'
  }>
}

/**
 * Calls the "tokenOfOwnerByIndex" function on the contract.
 * @param options - The options for the tokenOfOwnerByIndex function.
 * @returns The parsed result of the function call.
 * @example
 * ```
 * import { tokenOfOwnerByIndex } from "TODO";
 *
 * const result = await tokenOfOwnerByIndex({
 *  owner: ...,
 *  index: ...,
 * });
 *
 * ```
 */
export async function tokenOfOwnerByIndex(
  options: BaseTransactionOptions<TokenOfOwnerByIndexParams>
) {
  return readContract({
    contract: options.contract,
    method: [
      '0x2f745c59',
      [
        {
          internalType: 'address',
          name: 'owner',
          type: 'address',
        },
        {
          internalType: 'uint256',
          name: 'index',
          type: 'uint256',
        },
      ],
      [
        {
          internalType: 'uint256',
          name: '',
          type: 'uint256',
        },
      ],
    ],
    params: [options.owner, options.index],
  })
}

/**
 * Represents the parameters for the "tokenURI" function.
 */
export type TokenURIParams = {
  tokenId: AbiParameterToPrimitiveType<{
    internalType: 'uint256'
    name: 'tokenId'
    type: 'uint256'
  }>
}

/**
 * Calls the "tokenURI" function on the contract.
 * @param options - The options for the tokenURI function.
 * @returns The parsed result of the function call.
 * @example
 * ```
 * import { tokenURI } from "TODO";
 *
 * const result = await tokenURI({
 *  tokenId: ...,
 * });
 *
 * ```
 */
export async function tokenURI(
  options: BaseTransactionOptions<TokenURIParams>
) {
  return readContract({
    contract: options.contract,
    method: [
      '0xc87b56dd',
      [
        {
          internalType: 'uint256',
          name: 'tokenId',
          type: 'uint256',
        },
      ],
      [
        {
          internalType: 'string',
          name: '',
          type: 'string',
        },
      ],
    ],
    params: [options.tokenId],
  })
}

/**
 * Represents the parameters for the "tokensOfOwner" function.
 */
export type TokensOfOwnerParams = {
  owner: AbiParameterToPrimitiveType<{
    internalType: 'address'
    name: '_owner'
    type: 'address'
  }>
}

/**
 * Calls the "tokensOfOwner" function on the contract.
 * @param options - The options for the tokensOfOwner function.
 * @returns The parsed result of the function call.
 * @example
 * ```
 * import { tokensOfOwner } from "TODO";
 *
 * const result = await tokensOfOwner({
 *  owner: ...,
 * });
 *
 * ```
 */
export async function tokensOfOwner(
  options: BaseTransactionOptions<TokensOfOwnerParams>
) {
  return readContract({
    contract: options.contract,
    method: [
      '0x8462151c',
      [
        {
          internalType: 'address',
          name: '_owner',
          type: 'address',
        },
      ],
      [
        {
          internalType: 'uint256[]',
          name: 'ownerTokens',
          type: 'uint256[]',
        },
      ],
    ],
    params: [options.owner],
  })
}

/**
 * Calls the "totalSupply" function on the contract.
 * @param options - The options for the totalSupply function.
 * @returns The parsed result of the function call.
 * @example
 * ```
 * import { totalSupply } from "TODO";
 *
 * const result = await totalSupply();
 *
 * ```
 */
export async function totalSupply(options: BaseTransactionOptions) {
  return readContract({
    contract: options.contract,
    method: [
      '0x18160ddd',
      [],
      [
        {
          internalType: 'uint256',
          name: '',
          type: 'uint256',
        },
      ],
    ],
    params: [],
  })
}

/**
 * Contract write functions
 */

/**
 * Represents the parameters for the "addToCanMint" function.
 */
export type AddToCanMintParams = {
  wallet: AbiParameterToPrimitiveType<{
    internalType: 'address'
    name: '_wallet'
    type: 'address'
  }>
}

/**
 * Calls the "addToCanMint" function on the contract.
 * @param options - The options for the "addToCanMint" function.
 * @returns A prepared transaction object.
 * @example
 * ```
 * import { addToCanMint } from "TODO";
 *
 * const transaction = addToCanMint({
 *  wallet: ...,
 * });
 *
 * // Send the transaction
 * ...
 *
 * ```
 */
export function addToCanMint(
  options: BaseTransactionOptions<AddToCanMintParams>
) {
  return prepareContractCall({
    contract: options.contract,
    method: [
      '0x7c390bd8',
      [
        {
          internalType: 'address',
          name: '_wallet',
          type: 'address',
        },
      ],
      [],
    ],
    params: [options.wallet],
  })
}

/**
 * Represents the parameters for the "approve" function.
 */
export type ApproveParams = {
  to: AbiParameterToPrimitiveType<{
    internalType: 'address'
    name: 'to'
    type: 'address'
  }>
  tokenId: AbiParameterToPrimitiveType<{
    internalType: 'uint256'
    name: 'tokenId'
    type: 'uint256'
  }>
}

/**
 * Calls the "approve" function on the contract.
 * @param options - The options for the "approve" function.
 * @returns A prepared transaction object.
 * @example
 * ```
 * import { approve } from "TODO";
 *
 * const transaction = approve({
 *  to: ...,
 *  tokenId: ...,
 * });
 *
 * // Send the transaction
 * ...
 *
 * ```
 */
export function approve(options: BaseTransactionOptions<ApproveParams>) {
  return prepareContractCall({
    contract: options.contract,
    method: [
      '0x095ea7b3',
      [
        {
          internalType: 'address',
          name: 'to',
          type: 'address',
        },
        {
          internalType: 'uint256',
          name: 'tokenId',
          type: 'uint256',
        },
      ],
      [],
    ],
    params: [options.to, options.tokenId],
  })
}

/**
 * Represents the parameters for the "burnCredits" function.
 */
export type BurnCreditsParams = {
  tokenIds: AbiParameterToPrimitiveType<{
    internalType: 'uint256[]'
    name: '_tokenIds'
    type: 'uint256[]'
  }>
}

/**
 * Calls the "burnCredits" function on the contract.
 * @param options - The options for the "burnCredits" function.
 * @returns A prepared transaction object.
 * @example
 * ```
 * import { burnCredits } from "TODO";
 *
 * const transaction = burnCredits({
 *  tokenIds: ...,
 * });
 *
 * // Send the transaction
 * ...
 *
 * ```
 */
export function burnCredits(
  options: BaseTransactionOptions<BurnCreditsParams>
) {
  return prepareContractCall({
    contract: options.contract,
    method: [
      '0x49967b3e',
      [
        {
          internalType: 'uint256[]',
          name: '_tokenIds',
          type: 'uint256[]',
        },
      ],
      [],
    ],
    params: [options.tokenIds],
  })
}

/**
 * Calls the "destroy" function on the contract.
 * @param options - The options for the "destroy" function.
 * @returns A prepared transaction object.
 * @example
 * ```
 * import { destroy } from "TODO";
 *
 * const transaction = destroy();
 *
 * // Send the transaction
 * ...
 *
 * ```
 */
export function destroy(options: BaseTransactionOptions) {
  return prepareContractCall({
    contract: options.contract,
    method: ['0x83197ef0', [], []],
    params: [],
  })
}

/**
 * Represents the parameters for the "mintCredits" function.
 */
export type MintCreditsParams = {
  guid: AbiParameterToPrimitiveType<{
    internalType: 'bytes32[]'
    name: '_guid'
    type: 'bytes32[]'
  }>
  price: AbiParameterToPrimitiveType<{
    internalType: 'uint256'
    name: '_price'
    type: 'uint256'
  }>
  newExchangeRate: AbiParameterToPrimitiveType<{
    internalType: 'uint256'
    name: '_newExchangeRate'
    type: 'uint256'
  }>
  amount: AbiParameterToPrimitiveType<{
    internalType: 'uint256'
    name: 'amount'
    type: 'uint256'
  }>
  status: AbiParameterToPrimitiveType<{
    internalType: 'uint256'
    name: 'status'
    type: 'uint256'
  }>
  transferTo: AbiParameterToPrimitiveType<{
    internalType: 'address'
    name: 'transferTo'
    type: 'address'
  }>
}

/**
 * Calls the "mintCredits" function on the contract.
 * @param options - The options for the "mintCredits" function.
 * @returns A prepared transaction object.
 * @example
 * ```
 * import { mintCredits } from "TODO";
 *
 * const transaction = mintCredits({
 *  guid: ...,
 *  price: ...,
 *  newExchangeRate: ...,
 *  amount: ...,
 *  status: ...,
 *  transferTo: ...,
 * });
 *
 * // Send the transaction
 * ...
 *
 * ```
 */
export function mintCredits(
  options: BaseTransactionOptions<MintCreditsParams>
) {
  return prepareContractCall({
    contract: options.contract,
    method: [
      '0xbc0b1ec7',
      [
        {
          internalType: 'bytes32[]',
          name: '_guid',
          type: 'bytes32[]',
        },
        {
          internalType: 'uint256',
          name: '_price',
          type: 'uint256',
        },
        {
          internalType: 'uint256',
          name: '_newExchangeRate',
          type: 'uint256',
        },
        {
          internalType: 'uint256',
          name: 'amount',
          type: 'uint256',
        },
        {
          internalType: 'uint256',
          name: 'status',
          type: 'uint256',
        },
        {
          internalType: 'address',
          name: 'transferTo',
          type: 'address',
        },
      ],
      [
        {
          internalType: 'uint256[]',
          name: '',
          type: 'uint256[]',
        },
      ],
    ],
    params: [
      options.guid,
      options.price,
      options.newExchangeRate,
      options.amount,
      options.status,
      options.transferTo,
    ],
  })
}

/**
 * Calls the "pause" function on the contract.
 * @param options - The options for the "pause" function.
 * @returns A prepared transaction object.
 * @example
 * ```
 * import { pause } from "TODO";
 *
 * const transaction = pause();
 *
 * // Send the transaction
 * ...
 *
 * ```
 */
export function pause(options: BaseTransactionOptions) {
  return prepareContractCall({
    contract: options.contract,
    method: ['0x8456cb59', [], []],
    params: [],
  })
}

/**
 * Represents the parameters for the "purchaseCredits" function.
 */
export type PurchaseCreditsParams = {
  tokenIds: AbiParameterToPrimitiveType<{
    internalType: 'uint256[]'
    name: '_tokenIds'
    type: 'uint256[]'
  }>
  price: AbiParameterToPrimitiveType<{
    internalType: 'uint256'
    name: '_price'
    type: 'uint256'
  }>
  status: AbiParameterToPrimitiveType<{
    internalType: 'uint256'
    name: 'status'
    type: 'uint256'
  }>
}

/**
 * Calls the "purchaseCredits" function on the contract.
 * @param options - The options for the "purchaseCredits" function.
 * @returns A prepared transaction object.
 * @example
 * ```
 * import { purchaseCredits } from "TODO";
 *
 * const transaction = purchaseCredits({
 *  tokenIds: ...,
 *  price: ...,
 *  status: ...,
 * });
 *
 * // Send the transaction
 * ...
 *
 * ```
 */
export function purchaseCredits(
  options: BaseTransactionOptions<PurchaseCreditsParams>
) {
  return prepareContractCall({
    contract: options.contract,
    method: [
      '0xd9ecff79',
      [
        {
          internalType: 'uint256[]',
          name: '_tokenIds',
          type: 'uint256[]',
        },
        {
          internalType: 'uint256',
          name: '_price',
          type: 'uint256',
        },
        {
          internalType: 'uint256',
          name: 'status',
          type: 'uint256',
        },
      ],
      [
        {
          internalType: 'uint256[]',
          name: '',
          type: 'uint256[]',
        },
      ],
    ],
    params: [options.tokenIds, options.price, options.status],
  })
}

/**
 * Represents the parameters for the "removeFromCanMint" function.
 */
export type RemoveFromCanMintParams = {
  wallet: AbiParameterToPrimitiveType<{
    internalType: 'address'
    name: '_wallet'
    type: 'address'
  }>
}

/**
 * Calls the "removeFromCanMint" function on the contract.
 * @param options - The options for the "removeFromCanMint" function.
 * @returns A prepared transaction object.
 * @example
 * ```
 * import { removeFromCanMint } from "TODO";
 *
 * const transaction = removeFromCanMint({
 *  wallet: ...,
 * });
 *
 * // Send the transaction
 * ...
 *
 * ```
 */
export function removeFromCanMint(
  options: BaseTransactionOptions<RemoveFromCanMintParams>
) {
  return prepareContractCall({
    contract: options.contract,
    method: [
      '0x70532b44',
      [
        {
          internalType: 'address',
          name: '_wallet',
          type: 'address',
        },
      ],
      [],
    ],
    params: [options.wallet],
  })
}

/**
 * Represents the parameters for the "removeFromSplitValues" function.
 */
export type RemoveFromSplitValuesParams = {
  wallet: AbiParameterToPrimitiveType<{
    internalType: 'address'
    name: '_wallet'
    type: 'address'
  }>
}

/**
 * Calls the "removeFromSplitValues" function on the contract.
 * @param options - The options for the "removeFromSplitValues" function.
 * @returns A prepared transaction object.
 * @example
 * ```
 * import { removeFromSplitValues } from "TODO";
 *
 * const transaction = removeFromSplitValues({
 *  wallet: ...,
 * });
 *
 * // Send the transaction
 * ...
 *
 * ```
 */
export function removeFromSplitValues(
  options: BaseTransactionOptions<RemoveFromSplitValuesParams>
) {
  return prepareContractCall({
    contract: options.contract,
    method: [
      '0xba005cfa',
      [
        {
          internalType: 'address',
          name: '_wallet',
          type: 'address',
        },
      ],
      [],
    ],
    params: [options.wallet],
  })
}

/**
 * Represents the parameters for the "safeTransferFrom" function.
 */
export type SafeTransferFromParams = {
  from: AbiParameterToPrimitiveType<{
    internalType: 'address'
    name: 'from'
    type: 'address'
  }>
  to: AbiParameterToPrimitiveType<{
    internalType: 'address'
    name: 'to'
    type: 'address'
  }>
  tokenId: AbiParameterToPrimitiveType<{
    internalType: 'uint256'
    name: 'tokenId'
    type: 'uint256'
  }>
}

/**
 * Calls the "safeTransferFrom" function on the contract.
 * @param options - The options for the "safeTransferFrom" function.
 * @returns A prepared transaction object.
 * @example
 * ```
 * import { safeTransferFrom } from "TODO";
 *
 * const transaction = safeTransferFrom({
 *  from: ...,
 *  to: ...,
 *  tokenId: ...,
 * });
 *
 * // Send the transaction
 * ...
 *
 * ```
 */
export function safeTransferFrom(
  options: BaseTransactionOptions<SafeTransferFromParams>
) {
  return prepareContractCall({
    contract: options.contract,
    method: [
      '0x42842e0e',
      [
        {
          internalType: 'address',
          name: 'from',
          type: 'address',
        },
        {
          internalType: 'address',
          name: 'to',
          type: 'address',
        },
        {
          internalType: 'uint256',
          name: 'tokenId',
          type: 'uint256',
        },
      ],
      [],
    ],
    params: [options.from, options.to, options.tokenId],
  })
}

/**
 * Represents the parameters for the "setApprovalForAll" function.
 */
export type SetApprovalForAllParams = {
  operator: AbiParameterToPrimitiveType<{
    internalType: 'address'
    name: 'operator'
    type: 'address'
  }>
  approved: AbiParameterToPrimitiveType<{
    internalType: 'bool'
    name: 'approved'
    type: 'bool'
  }>
}

/**
 * Calls the "setApprovalForAll" function on the contract.
 * @param options - The options for the "setApprovalForAll" function.
 * @returns A prepared transaction object.
 * @example
 * ```
 * import { setApprovalForAll } from "TODO";
 *
 * const transaction = setApprovalForAll({
 *  operator: ...,
 *  approved: ...,
 * });
 *
 * // Send the transaction
 * ...
 *
 * ```
 */
export function setApprovalForAll(
  options: BaseTransactionOptions<SetApprovalForAllParams>
) {
  return prepareContractCall({
    contract: options.contract,
    method: [
      '0xa22cb465',
      [
        {
          internalType: 'address',
          name: 'operator',
          type: 'address',
        },
        {
          internalType: 'bool',
          name: 'approved',
          type: 'bool',
        },
      ],
      [],
    ],
    params: [options.operator, options.approved],
  })
}

/**
 * Represents the parameters for the "setExecutiveOfficer" function.
 */
export type SetExecutiveOfficerParams = {
  executiveOfficerAddress: AbiParameterToPrimitiveType<{
    internalType: 'address payable'
    name: '_executiveOfficerAddress'
    type: 'address'
  }>
}

/**
 * Calls the "setExecutiveOfficer" function on the contract.
 * @param options - The options for the "setExecutiveOfficer" function.
 * @returns A prepared transaction object.
 * @example
 * ```
 * import { setExecutiveOfficer } from "TODO";
 *
 * const transaction = setExecutiveOfficer({
 *  executiveOfficerAddress: ...,
 * });
 *
 * // Send the transaction
 * ...
 *
 * ```
 */
export function setExecutiveOfficer(
  options: BaseTransactionOptions<SetExecutiveOfficerParams>
) {
  return prepareContractCall({
    contract: options.contract,
    method: [
      '0xec13df6b',
      [
        {
          internalType: 'address payable',
          name: '_executiveOfficerAddress',
          type: 'address',
        },
      ],
      [],
    ],
    params: [options.executiveOfficerAddress],
  })
}

/**
 * Represents the parameters for the "setFinancialOfficer" function.
 */
export type SetFinancialOfficerParams = {
  financialOfficerAddress: AbiParameterToPrimitiveType<{
    internalType: 'address payable'
    name: '_financialOfficerAddress'
    type: 'address'
  }>
}

/**
 * Calls the "setFinancialOfficer" function on the contract.
 * @param options - The options for the "setFinancialOfficer" function.
 * @returns A prepared transaction object.
 * @example
 * ```
 * import { setFinancialOfficer } from "TODO";
 *
 * const transaction = setFinancialOfficer({
 *  financialOfficerAddress: ...,
 * });
 *
 * // Send the transaction
 * ...
 *
 * ```
 */
export function setFinancialOfficer(
  options: BaseTransactionOptions<SetFinancialOfficerParams>
) {
  return prepareContractCall({
    contract: options.contract,
    method: [
      '0xd978a0d3',
      [
        {
          internalType: 'address payable',
          name: '_financialOfficerAddress',
          type: 'address',
        },
      ],
      [],
    ],
    params: [options.financialOfficerAddress],
  })
}

/**
 * Represents the parameters for the "setMintPrice" function.
 */
export type SetMintPriceParams = {
  amount: AbiParameterToPrimitiveType<{
    internalType: 'uint256'
    name: 'amount'
    type: 'uint256'
  }>
}

/**
 * Calls the "setMintPrice" function on the contract.
 * @param options - The options for the "setMintPrice" function.
 * @returns A prepared transaction object.
 * @example
 * ```
 * import { setMintPrice } from "TODO";
 *
 * const transaction = setMintPrice({
 *  amount: ...,
 * });
 *
 * // Send the transaction
 * ...
 *
 * ```
 */
export function setMintPrice(
  options: BaseTransactionOptions<SetMintPriceParams>
) {
  return prepareContractCall({
    contract: options.contract,
    method: [
      '0xf4a0a528',
      [
        {
          internalType: 'uint256',
          name: 'amount',
          type: 'uint256',
        },
      ],
      [],
    ],
    params: [options.amount],
  })
}

/**
 * Represents the parameters for the "setSplitValues" function.
 */
export type SetSplitValuesParams = {
  wallet: AbiParameterToPrimitiveType<{
    internalType: 'address'
    name: '_wallet'
    type: 'address'
  }>
  fee: AbiParameterToPrimitiveType<{
    internalType: 'uint256'
    name: 'fee'
    type: 'uint256'
  }>
}

/**
 * Calls the "setSplitValues" function on the contract.
 * @param options - The options for the "setSplitValues" function.
 * @returns A prepared transaction object.
 * @example
 * ```
 * import { setSplitValues } from "TODO";
 *
 * const transaction = setSplitValues({
 *  wallet: ...,
 *  fee: ...,
 * });
 *
 * // Send the transaction
 * ...
 *
 * ```
 */
export function setSplitValues(
  options: BaseTransactionOptions<SetSplitValuesParams>
) {
  return prepareContractCall({
    contract: options.contract,
    method: [
      '0xa70c066b',
      [
        {
          internalType: 'address',
          name: '_wallet',
          type: 'address',
        },
        {
          internalType: 'uint256',
          name: 'fee',
          type: 'uint256',
        },
      ],
      [],
    ],
    params: [options.wallet, options.fee],
  })
}

/**
 * Represents the parameters for the "setTransferFee" function.
 */
export type SetTransferFeeParams = {
  amount: AbiParameterToPrimitiveType<{
    internalType: 'uint256'
    name: 'amount'
    type: 'uint256'
  }>
}

/**
 * Calls the "setTransferFee" function on the contract.
 * @param options - The options for the "setTransferFee" function.
 * @returns A prepared transaction object.
 * @example
 * ```
 * import { setTransferFee } from "TODO";
 *
 * const transaction = setTransferFee({
 *  amount: ...,
 * });
 *
 * // Send the transaction
 * ...
 *
 * ```
 */
export function setTransferFee(
  options: BaseTransactionOptions<SetTransferFeeParams>
) {
  return prepareContractCall({
    contract: options.contract,
    method: [
      '0x8f02bb5b',
      [
        {
          internalType: 'uint256',
          name: 'amount',
          type: 'uint256',
        },
      ],
      [],
    ],
    params: [options.amount],
  })
}

/**
 * Represents the parameters for the "setWalletSpecificMintPrice" function.
 */
export type SetWalletSpecificMintPriceParams = {
  wallet: AbiParameterToPrimitiveType<{
    internalType: 'address'
    name: '_wallet'
    type: 'address'
  }>
  price: AbiParameterToPrimitiveType<{
    internalType: 'uint256'
    name: '_price'
    type: 'uint256'
  }>
}

/**
 * Calls the "setWalletSpecificMintPrice" function on the contract.
 * @param options - The options for the "setWalletSpecificMintPrice" function.
 * @returns A prepared transaction object.
 * @example
 * ```
 * import { setWalletSpecificMintPrice } from "TODO";
 *
 * const transaction = setWalletSpecificMintPrice({
 *  wallet: ...,
 *  price: ...,
 * });
 *
 * // Send the transaction
 * ...
 *
 * ```
 */
export function setWalletSpecificMintPrice(
  options: BaseTransactionOptions<SetWalletSpecificMintPriceParams>
) {
  return prepareContractCall({
    contract: options.contract,
    method: [
      '0x319a746b',
      [
        {
          internalType: 'address',
          name: '_wallet',
          type: 'address',
        },
        {
          internalType: 'uint256',
          name: '_price',
          type: 'uint256',
        },
      ],
      [],
    ],
    params: [options.wallet, options.price],
  })
}

/**
 * Represents the parameters for the "setWalletSpecificTransferFee" function.
 */
export type SetWalletSpecificTransferFeeParams = {
  wallet: AbiParameterToPrimitiveType<{
    internalType: 'address'
    name: '_wallet'
    type: 'address'
  }>
  fee: AbiParameterToPrimitiveType<{
    internalType: 'uint256'
    name: '_fee'
    type: 'uint256'
  }>
}

/**
 * Calls the "setWalletSpecificTransferFee" function on the contract.
 * @param options - The options for the "setWalletSpecificTransferFee" function.
 * @returns A prepared transaction object.
 * @example
 * ```
 * import { setWalletSpecificTransferFee } from "TODO";
 *
 * const transaction = setWalletSpecificTransferFee({
 *  wallet: ...,
 *  fee: ...,
 * });
 *
 * // Send the transaction
 * ...
 *
 * ```
 */
export function setWalletSpecificTransferFee(
  options: BaseTransactionOptions<SetWalletSpecificTransferFeeParams>
) {
  return prepareContractCall({
    contract: options.contract,
    method: [
      '0x29fd499b',
      [
        {
          internalType: 'address',
          name: '_wallet',
          type: 'address',
        },
        {
          internalType: 'uint256',
          name: '_fee',
          type: 'uint256',
        },
      ],
      [],
    ],
    params: [options.wallet, options.fee],
  })
}

/**
 * Represents the parameters for the "transferCredits" function.
 */
export type TransferCreditsParams = {
  tokenIds: AbiParameterToPrimitiveType<{
    internalType: 'uint256[]'
    name: '_tokenIds'
    type: 'uint256[]'
  }>
  transferTo: AbiParameterToPrimitiveType<{
    internalType: 'address'
    name: 'transferTo'
    type: 'address'
  }>
}

/**
 * Calls the "transferCredits" function on the contract.
 * @param options - The options for the "transferCredits" function.
 * @returns A prepared transaction object.
 * @example
 * ```
 * import { transferCredits } from "TODO";
 *
 * const transaction = transferCredits({
 *  tokenIds: ...,
 *  transferTo: ...,
 * });
 *
 * // Send the transaction
 * ...
 *
 * ```
 */
export function transferCredits(
  options: BaseTransactionOptions<TransferCreditsParams>
) {
  return prepareContractCall({
    contract: options.contract,
    method: [
      '0x8950099d',
      [
        {
          internalType: 'uint256[]',
          name: '_tokenIds',
          type: 'uint256[]',
        },
        {
          internalType: 'address',
          name: 'transferTo',
          type: 'address',
        },
      ],
      [
        {
          internalType: 'uint256[]',
          name: '',
          type: 'uint256[]',
        },
      ],
    ],
    params: [options.tokenIds, options.transferTo],
  })
}

/**
 * Represents the parameters for the "transferFrom" function.
 */
export type TransferFromParams = {
  from: AbiParameterToPrimitiveType<{
    internalType: 'address'
    name: 'from'
    type: 'address'
  }>
  to: AbiParameterToPrimitiveType<{
    internalType: 'address'
    name: 'to'
    type: 'address'
  }>
  tokenId: AbiParameterToPrimitiveType<{
    internalType: 'uint256'
    name: 'tokenId'
    type: 'uint256'
  }>
}

/**
 * Calls the "transferFrom" function on the contract.
 * @param options - The options for the "transferFrom" function.
 * @returns A prepared transaction object.
 * @example
 * ```
 * import { transferFrom } from "TODO";
 *
 * const transaction = transferFrom({
 *  from: ...,
 *  to: ...,
 *  tokenId: ...,
 * });
 *
 * // Send the transaction
 * ...
 *
 * ```
 */
export function transferFrom(
  options: BaseTransactionOptions<TransferFromParams>
) {
  return prepareContractCall({
    contract: options.contract,
    method: [
      '0x23b872dd',
      [
        {
          internalType: 'address',
          name: 'from',
          type: 'address',
        },
        {
          internalType: 'address',
          name: 'to',
          type: 'address',
        },
        {
          internalType: 'uint256',
          name: 'tokenId',
          type: 'uint256',
        },
      ],
      [],
    ],
    params: [options.from, options.to, options.tokenId],
  })
}

/**
 * Calls the "unpause" function on the contract.
 * @param options - The options for the "unpause" function.
 * @returns A prepared transaction object.
 * @example
 * ```
 * import { unpause } from "TODO";
 *
 * const transaction = unpause();
 *
 * // Send the transaction
 * ...
 *
 * ```
 */
export function unpause(options: BaseTransactionOptions) {
  return prepareContractCall({
    contract: options.contract,
    method: ['0x3f4ba83a', [], []],
    params: [],
  })
}

/**
 * Represents the parameters for the "updateTokens" function.
 */
export type UpdateTokensParams = {
  tokenIds: AbiParameterToPrimitiveType<{
    internalType: 'uint256[]'
    name: '_tokenIds'
    type: 'uint256[]'
  }>
  price: AbiParameterToPrimitiveType<{
    internalType: 'uint256'
    name: 'price'
    type: 'uint256'
  }>
  newExchangeRate: AbiParameterToPrimitiveType<{
    internalType: 'uint256'
    name: '_newExchangeRate'
    type: 'uint256'
  }>
  status: AbiParameterToPrimitiveType<{
    internalType: 'uint256'
    name: 'status'
    type: 'uint256'
  }>
  erAdjust: AbiParameterToPrimitiveType<{
    internalType: 'bool'
    name: 'erAdjust'
    type: 'bool'
  }>
}

/**
 * Calls the "updateTokens" function on the contract.
 * @param options - The options for the "updateTokens" function.
 * @returns A prepared transaction object.
 * @example
 * ```
 * import { updateTokens } from "TODO";
 *
 * const transaction = updateTokens({
 *  tokenIds: ...,
 *  price: ...,
 *  newExchangeRate: ...,
 *  status: ...,
 *  erAdjust: ...,
 * });
 *
 * // Send the transaction
 * ...
 *
 * ```
 */
export function updateTokens(
  options: BaseTransactionOptions<UpdateTokensParams>
) {
  return prepareContractCall({
    contract: options.contract,
    method: [
      '0x4ee0065b',
      [
        {
          internalType: 'uint256[]',
          name: '_tokenIds',
          type: 'uint256[]',
        },
        {
          internalType: 'uint256',
          name: 'price',
          type: 'uint256',
        },
        {
          internalType: 'uint256',
          name: '_newExchangeRate',
          type: 'uint256',
        },
        {
          internalType: 'uint256',
          name: 'status',
          type: 'uint256',
        },
        {
          internalType: 'bool',
          name: 'erAdjust',
          type: 'bool',
        },
      ],
      [],
    ],
    params: [
      options.tokenIds,
      options.price,
      options.newExchangeRate,
      options.status,
      options.erAdjust,
    ],
  })
}

/**
 * Calls the "withdrawBalance" function on the contract.
 * @param options - The options for the "withdrawBalance" function.
 * @returns A prepared transaction object.
 * @example
 * ```
 * import { withdrawBalance } from "TODO";
 *
 * const transaction = withdrawBalance();
 *
 * // Send the transaction
 * ...
 *
 * ```
 */
export function withdrawBalance(options: BaseTransactionOptions) {
  return prepareContractCall({
    contract: options.contract,
    method: ['0x5fd8c710', [], []],
    params: [],
  })
}
